// ViewModalFilterRow.jsx

import React from 'react';
import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';

// Utilities
import { getOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';

// CSS
import './ViewModalFilterRow.css';

// Icons
import Icon from 'svg/Icon';

// Components
import {
    applyOperatorChange,
    buildFilterForFieldKey,
    ENUM_MULTI_OPERATORS,
    getFilterOperatorOptions,
    getValueInputKind,
    isDateLikeFieldType,
    isRelativeTemporalOperator,
    normalizeCalendarUnit,
    normalizeFilterJoin,
    normalizeRelativeUnit,
    operatorNeedsUnit,
    operatorNeedsValue,
    RELATIVE_CALENDAR_UNITS,
    RELATIVE_DATE_UNITS,
    resolveFieldType,
    VALUELESS_OPERATORS,
} from '../../viewModalFilters';
import CheckboxList from 'components/checkboxlist/CheckboxList';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';
import NodelistFilterValueInput from './NodelistFilterValueInput';

/**
 * ViewModalFilterRow
 *
 * Renders a single filter row and owns the row-level update behavior.
 * This keeps ViewModal from needing to manage each field-type input.
 *
 * @param {object} filter - The filter object represented by this row.
 * @param {number} index - The index of this row in the filters list.
 * @param {boolean} isLastRow - Whether this is the last row in the list.
 * @param {array} fieldOptions - Available field options for filter selection.
 * @param {Map} filterFieldMap - Field metadata map keyed by field key.
 * @param {function} setFilters - State setter for the filters array.
 * @param {object} theme - Theme object used for dynamic color styling.
 *
 * @returns {ReactNode} The rendered filter row.
 */
const ViewModalFilterRow = ({
    filter,
    index,
    isLastRow,
    fieldOptions,
    filterFieldMap,
    setFilters,
    theme,
}) => {

    const fieldDef = filterFieldMap.get(filter.field);
    const fieldType = resolveFieldType(fieldDef, filter?.type);
    const valueInputKind = getValueInputKind(fieldType);
    const operatorOptions = getFilterOperatorOptions(fieldType);
    const normalizedEnumOptions = valueInputKind === 'enum'
        ? resolveEnumOptionsForField(fieldDef, 'en')
        : [];
    const operator = filter.operator || 'is';
    const isDateField = isDateLikeFieldType(fieldType);
    const isRelativeDate = isDateField && isRelativeTemporalOperator(fieldType, operator);
    const requiresValue = operatorNeedsValue(fieldType, operator);
    const requiresUnit = operatorNeedsUnit(fieldType, operator);
    const showValueInput = !VALUELESS_OPERATORS.has(operator) && !isRelativeDate;
    const isBetweenNumber = valueInputKind === 'number' && operator === 'between';
    const isEnumMulti = (isEnumCompatibleFieldType(fieldType) || fieldType === 'multiselect' || fieldType === 'tags' || fieldType === 'relation')
        && ENUM_MULTI_OPERATORS.has(operator);
    const isNodelistValue = valueInputKind === 'relation';
    const selectedEnumValues = Array.isArray(filter.value) ? filter.value : [];
    const betweenValue = filter?.value && typeof filter.value === 'object' && !Array.isArray(filter.value)
        ? filter.value
        : { min: filter?.value ?? '', max: '' };
    const joinValue = normalizeFilterJoin(filter?.joinToNext);

    // ----- UTILITIES ----- //

    /**
     * Updates only the filter row at the current index.
     *
     * @param {function} updater - Function that returns the next row value.
     */

    const updateFilterAtIndex = (updater) => {
        setFilters((prev) => prev.map((item, idx) => (
            idx === index ? updater(item) : item
        )));
    };

    // ----- HANDLERS ----- //

    /**
     * Removes the filter row at the current index.
     */

    const handleRemoveFilter = () => {
        setFilters((prev) => prev.filter((_, idx) => idx !== index));
    };

    // ----- RENDER ----- //

    return (
        <div className="view-modal-row">

            <SelectInput
              style={{
                    fontSize: '12px',
                }}
              value={filter.field || ''}
              options={fieldOptions}
              onChange={(e) => {
                    const nextFieldKey = e.target.value;
                    const nextField = filterFieldMap.get(nextFieldKey);
                    const nextFilter = buildFilterForFieldKey(nextFieldKey, nextField);
                    updateFilterAtIndex((item) => ({ ...item, ...nextFilter }));
                }}
            />

            <SelectInput
              style={{
                    fontSize: '12px',
                }}
              value={operator}
              options={operatorOptions}
              onChange={(e) => {
                    const nextOperator = e.target.value;
                    updateFilterAtIndex((item) => applyOperatorChange({
                        currentFilter: item,
                        fieldDef,
                        fieldType,
                        operator: nextOperator,
                    }));
                }}
            />

            {isRelativeDate ? (
                requiresValue && requiresUnit ? (
                    <div className="view-modal-relative-group">
                        <TextInput
                          style={{
                                fontSize: '12px',
                            }}
                          placeholder="Value"
                          type="number"
                          value={filter.value ?? ''}
                          onChange={(e) => {
                                const nextValue = e.target.value;
                                updateFilterAtIndex((item) => ({ ...item, value: nextValue }));
                            }}
                        />
                        <SelectInput
                          style={{
                                fontSize: '12px',
                            }}
                          value={operator === 'is_this' || operator === 'overlaps_this'
                                ? (normalizeCalendarUnit(filter.unit) || 'week')
                                : (normalizeRelativeUnit(filter.unit) || 'days')}
                          options={operator === 'is_this' || operator === 'overlaps_this'
                                ? RELATIVE_CALENDAR_UNITS
                                : RELATIVE_DATE_UNITS}
                          onChange={(e) => {
                                const nextUnit = e.target.value;
                                updateFilterAtIndex((item) => ({ ...item, unit: nextUnit }));
                            }}
                        />
                    </div>
                ) : requiresUnit ? (
                    <SelectInput
                      style={{
                            fontSize: '12px',
                        }}
                      value={normalizeCalendarUnit(filter.unit) || 'week'}
                      options={RELATIVE_CALENDAR_UNITS}
                      onChange={(e) => {
                            const nextUnit = e.target.value;
                            updateFilterAtIndex((item) => ({ ...item, unit: nextUnit }));
                        }}
                    />
                ) : (
                    <div className="view-modal-row-spacer" />
                )
            ) : showValueInput ? (
                isBetweenNumber ? (
                    <div className="view-modal-between-group">
                        <TextInput
                          style={{
                                fontSize: '12px',
                            }}
                          placeholder="Min"
                          type="number"
                          value={betweenValue.min ?? ''}
                          onChange={(e) => {
                                const nextMin = e.target.value;
                                updateFilterAtIndex((item) => ({
                                    ...item,
                                    value: {
                                        max: betweenValue.max ?? '',
                                        min: nextMin,
                                    },
                                }));
                            }}
                        />
                        <TextInput
                          style={{
                                fontSize: '12px',
                            }}
                          placeholder="Max"
                          type="number"
                          value={betweenValue.max ?? ''}
                          onChange={(e) => {
                                const nextMax = e.target.value;
                                updateFilterAtIndex((item) => ({
                                    ...item,
                                    value: {
                                        max: nextMax,
                                        min: betweenValue.min ?? '',
                                    },
                                }));
                            }}
                        />
                    </div>
                ) : isNodelistValue ? (
                    <NodelistFilterValueInput
                      fieldDef={fieldDef}
                      multi={isEnumMulti}
                      value={isEnumMulti ? selectedEnumValues : (filter.value || '')}
                      onChange={(nextValue) => {
                            updateFilterAtIndex((item) => ({ ...item, value: nextValue }));
                      }}
                    />
                ) : isEnumMulti ? (
                    <CheckboxList
                      options={normalizedEnumOptions.map((option) => ({
                            value: option.value,
                            label: getOptionLabel(option, 'en'),
                        }))}
                      values={selectedEnumValues}
                      checkboxSize={18}
                      multiSelect
                      onChange={(nextValues) => {
                            updateFilterAtIndex((item) => ({ ...item, value: nextValues }));
                      }}
                    />
                ) : valueInputKind === 'boolean' ? (
                    <SelectInput
                      style={{
                            fontSize: '12px',
                        }}
                      value={String(filter.value ?? true)}
                      options={[
                            { value: 'true', label: 'True' },
                            { value: 'false', label: 'False' },
                        ]}
                      onChange={(e) => {
                            const nextValue = e.target.value === 'true';
                            updateFilterAtIndex((item) => ({ ...item, value: nextValue }));
                        }}
                    />
                ) : valueInputKind === 'enum' ? (
                    <SelectInput
                      style={{
                            fontSize: '12px',
                        }}
                      value={filter.value || ''}
                      options={[
                            { value: '', label: '' },
                            ...normalizedEnumOptions.map((option) => ({
                                value: option.value,
                                label: getOptionLabel(option, 'en'),
                            })),
                        ]}
                      onChange={(e) => {
                            const nextValue = e.target.value;
                            updateFilterAtIndex((item) => ({ ...item, value: nextValue }));
                        }}
                    />
                ) : (
                    <TextInput
                      style={{
                            fontSize: '12px',
                        }}
                      placeholder="Value"
                      type={valueInputKind}
                      value={filter.value ?? ''}
                      onChange={(e) => {
                            const nextValue = e.target.value;
                            updateFilterAtIndex((item) => ({ ...item, value: nextValue }));
                        }}
                    />
                )
            ) : (
                <div className="view-modal-row-spacer" />
            )}

            {!isLastRow ? (
                <SelectInput
                  style={{
                        fontSize: '12px',
                    }}
                  value={joinValue}
                  options={[
                        { value: 'and', label: 'and' },
                        { value: 'or', label: 'or' },
                    ]}
                  onChange={(e) => {
                        const nextJoin = normalizeFilterJoin(e.target.value);
                        updateFilterAtIndex((item) => ({ ...item, joinToNext: nextJoin }));
                    }}
                />
            ) : (
                <div className="view-modal-row-spacer" />
            )}

            <div
              className="view-modal-remove-button"
              style={{
                    color: theme.foregroundMuted,
                }}
              onClick={handleRemoveFilter}
            >
                <Icon
                  color={theme.foregroundMuted}
                  height={16}
                  iconKey="do_not_disturb_on"
                  width={16}
                />
            </div>
        </div>
    );
};

// Dev Tools
ViewModalFilterRow.displayName = 'ViewModalFilterRow';

export default ViewModalFilterRow;
